import React, { useState } from 'react';
import { useContextSelector } from 'use-context-selector';

import SystemUnavailable from '../../components/SystemUnavailable';
import LoadingOverlay from '../../components/LoadingOverlay';
import ContainerPage from '../../components/ContainerPage';
import { UnitSelectorSpecialist } from './components/UnitSelectorSpecialist';
import { ServiceLocationForm } from './components/ServiceLocationForm';
import { GenericModalActions } from './components/GenericModalActions';
import { CoverageSelector } from './components/CoverageSelector';
import { UnitSelector } from './components/UnitSelector';
import { ActionButton } from './components/ActionButton';
import HeadPage from './components/HeadPage';
import { ModalServiceUnavailable as ModalEAVServiceUnavailable } from './components/ModalServiceUnavailable';

import { typeLocationSchedule } from '../../models/Schedule';

import { SpecialistContext } from '../../contexts/SpecialistContext';
import { ScheduleContext } from '../../contexts/ScheduleContext';
import { CartContext } from '../../contexts/CartContext';
import { PatientContext } from '../../contexts/PatientContext';
import { ServiceContext } from '../../contexts/ServiceContext';
import { ModuleContext } from '../../contexts/ModuleContext';
import { PaymentContext } from '../../contexts/PaymentContext';
import { RouterContext } from '../../contexts/RouterContext';
import { ModalsContext } from '../../contexts/ModalsContext';
import { useAttendaceTypeSpecialist } from './hooks/useAttendanceTypeSpecialist';
import { useServiceTypePage } from './index.model';
import { ContainerBody } from './styles';
import { FirebaseEvents } from '../../utils/enums/firebase-events';
import { useAnalytics } from '../../hooks/useAnalytics';

const ServiceTypePage = () => {
  const { setEligibilityPlans, eligibilityPlans } = useContextSelector(PaymentContext, state => state);
  const { findProduct, changeProduct } = useContextSelector(CartContext, state => state);
  const { selectedItemToSchedule } = useContextSelector(ModuleContext, state => state);
  const { selectedSpecialist } = useContextSelector(SpecialistContext, state => state);
  const { selectedService } = useContextSelector(ServiceContext, state => state);
  const { openModal } = useContextSelector(ModalsContext, state => state);
  const { goTo, goBack } = useContextSelector(RouterContext, state => state);
  const { patientSelected, addPatientList } = useContextSelector(PatientContext, state => state);
  const {
    addUnits,
    addUnitDatesAvailable,
    setScheduleSpecialistSelected,
    setEavSlotsData,
    getNotScheduledProducts,
    scheduleSpecialistSelected,
    changeScheduleSpecialist,
    changeSchedule,
    selectedItem,
    setUnitDatesAvailable
  } = useContextSelector(ScheduleContext, state => state);

  const scheduleProduct = findProduct(selectedItem?.productId || '');
  const [loadingChangeProduct, setLoadingChangeProduct] = React.useState<boolean>(false);
  const [typeLocation, setTypeLocation] = React.useState<any>(false);
  const [eavLocation, setEavLocation] = React.useState<any>(false);
  const [selectedCoverageSpecialist, setSelectedCoverageSpecialist] = useState<{
    presencial: boolean;
    telemedicine: boolean;
    eav: boolean;
  }>({ presencial: false, telemedicine: false, eav: false });

  const { logEvent } = useAnalytics();

  const { coveragesSpecialist, specialistUnitSelected, onSelectUnitSpecialist, setSpecialistUnitSelected } =
    useAttendaceTypeSpecialist({
      patientSelected,
      scheduleSpecialistSelected,
      selectedItemToSchedule,
      setLoadingChangeProduct,
      selectedSpecialist
    });

  const {
    handleRemoveResource,
    loading,
    selectCoverage,
    selectedUnit,
    coverageUnit,
    selectedCoverage,
    closedToScheduleUnits,
    openToScheduleUnits,
    schedule,
    setUnitToSchedule,
    genericModal,
    isModalSystemUnavailableVisible,
    resetCoverageSelection,
    setIsModalSystemUnavailableVisible,
    setSpecificUnitsResources,
    submitButtonRef,
    showModalServiceUnavailable,
    setShowModalServiceUnavailable
  } = useServiceTypePage({
    addPatientList,
    changeProduct,
    eligibilityPlans,
    setEligibilityPlans,
    changeSchedule,
    goTo,
    selectedSchedule: selectedItem,
    openModal,
    changeScheduleSpecialist,
    scheduleSpecialistSelected,
    setScheduleSpecialistSelected,
    loadingChangeProduct,
    addUnits,
    addUnitDatesAvailable,
    patientSelected,
    productRules: scheduleProduct?.rules ?? []
  });

  if (!selectedItem) return null;

  return (
    <ContainerPage
      childrenFooter={
        selectedItem ? (
          <ActionButton
            ref={submitButtonRef}
            selectedItemToSchedule={selectedItemToSchedule}
            specialistUnitSelected={specialistUnitSelected}
            selectedUnit={selectedUnit}
            eavLocation={eavLocation}
            setLoadingChangeProduct={setLoadingChangeProduct}
            coverageUnit={coverageUnit}
            // patientList={patientList}
            findProduct={findProduct}
            // findPatiendById={findPatiendById}
            selectedCoverage={selectedCoverage}
            coveragesSpecialist={coveragesSpecialist}
            selectedSpecialist={selectedSpecialist}
            selectedCoverageSpecialist={selectedCoverageSpecialist}
            setEavSlotsData={setEavSlotsData}
            getNotScheduledProducts={getNotScheduledProducts}
            scheduleSpecialistSelected={scheduleSpecialistSelected}
            changeScheduleSpecialist={changeScheduleSpecialist}
            changeSchedule={changeSchedule}
            selectedItem={selectedItem}
            patient={patientSelected}
          />
        ) : null
      }
    >
      <ContainerBody>
        <>
          <HeadPage
            productRules={scheduleProduct?.rules}
            handleGoBack={() => {
              setUnitDatesAvailable([]);
              goBack();
            }}
          />
          {selectedItemToSchedule && (
            <CoverageSelector
              selectedCoverage={selectedCoverage}
              coverages={scheduleProduct?.coverages}
              handleSelectCoverage={selectCoverage}
              productName={scheduleProduct?.name}
              selectedItemToSchedule={selectedItemToSchedule}
              coveragesSpecialists={coveragesSpecialist}
              selectedCoverageSpecialist={selectedCoverageSpecialist}
              setSelectedCoverageSpecialist={args => {
                logEvent({
                  eventName: FirebaseEvents.schedule_selected_attendance_type,
                  eventParams: {
                    product_id: selectedItem?.productId,
                    product_name: selectedItem?.productName,
                    service_id: selectedService.id,
                    service_name: selectedService.name,
                    attendanceType: args.presencial ? 'PRESENTIAL' : args.telemedicine ? 'TELEMEDICINE' : 'EAV'
                  }
                });

                setSpecialistUnitSelected(undefined);
                setSelectedCoverageSpecialist(args);
              }}
            />
          )}
        </>

        {selectedItemToSchedule === 'SPECIALIST' && (
          <>
            {selectedCoverageSpecialist.presencial && (
              <UnitSelectorSpecialist
                units={coveragesSpecialist.presential}
                selectedUnit={specialistUnitSelected}
                onSelectUnit={onSelectUnitSpecialist}
              />
            )}
            {selectedCoverageSpecialist.telemedicine && (
              <UnitSelectorSpecialist
                units={coveragesSpecialist.telemedicine}
                selectedUnit={specialistUnitSelected}
                onSelectUnit={onSelectUnitSpecialist}
                showTeleWarning
              />
            )}
            {selectedCoverageSpecialist.eav && (
              <UnitSelectorSpecialist
                units={coveragesSpecialist.eav}
                selectedUnit={specialistUnitSelected}
                onSelectUnit={onSelectUnitSpecialist}
              />
            )}
          </>
        )}

        {(selectedCoverage?.care_type === typeLocationSchedule.PRESENTIAL ||
          selectedCoverage?.care_type === typeLocationSchedule.TELEMEDICINE ||
          selectedCoverage?.care_type === typeLocationSchedule.DRIVE_THRU ||
          selectedCoverage?.care_type === typeLocationSchedule.PROCEDURE) &&
          !loading && (
            <UnitSelector
              openToScheduleUnits={openToScheduleUnits}
              closedToScheduleUnits={closedToScheduleUnits}
              typePayment={String(selectedItem?.payment?.type)}
              selectedLocation={selectedUnit}
              onLocationSelected={setUnitToSchedule}
              careType={selectedCoverage.care_type}
              hasResource={!!schedule?.resource}
              schedule={selectedItem}
            />
          )}

        {/* {selectedCoverage?.care_type === typeLocationSchedule.EINSTEIN_TO_YOU && (
          <ServiceLocationForm
            location={eavLocation}
            setValidForm={valid => {
              if (!valid) setEavLocation(null);
            }}
            setLocation={loc => {
              setEavLocation(loc);
              if (loc) {
                setTypeLocation({ ...typeLocation, address: loc });
              }
            }}
          />
        )} */}
      </ContainerBody>
      <LoadingOverlay loading={loading} />
      <GenericModalActions
        genericModal={genericModal}
        setLoadingChangeProduct={setLoadingChangeProduct}
        handleRemoveResource={handleRemoveResource}
        selectedService={selectedService}
        resetCoverageSelection={resetCoverageSelection}
        patientSelected={patientSelected}
        setSpecificUnitsResources={setSpecificUnitsResources}
        onCloseModal={() => {
          resetCoverageSelection();
        }}
      />
      <ModalEAVServiceUnavailable
        visible={showModalServiceUnavailable}
        onClickWhatsapp={() => {
          window.open('https://wa.me/551136202550');
        }}
        onClickClose={() => setShowModalServiceUnavailable(false)}
      />
      <SystemUnavailable
        visible={isModalSystemUnavailableVisible}
        onClose={() => {
          setIsModalSystemUnavailableVisible(false);
        }}
      />
    </ContainerPage>
  );
};

export default ServiceTypePage;
